import React from 'react';

import { Pill, PillVariant } from '@/components/common/Pill';

/**
 * Priority level to pill variant mapping
 * Uses the reusable Pill component for consistency
 */
const PRIORITY_VARIANTS: Record<string, PillVariant> = {
  immediate: 'red',
  urgent: 'orange',
  'past due': 'pink',
  normal: 'gray',
  low: 'gray',
};

interface PriorityPillProps {
  /** Priority level (e.g., "immediate", "urgent", "past due", "normal", "low") */
  priority: string;
  /** Size of the pill (default: sm for table usage) */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Reusable priority pill component for task priorities
 *
 * Uses the base Pill component for consistency across the application.
 * Defaults to 'sm' size for table usage.
 *
 * Usage:
 * ```tsx
 * <PriorityPill priority="immediate" />
 * <PriorityPill priority="urgent" />
 * <PriorityPill priority="normal" />
 * <PriorityPill priority="low" />
 * ```
 *
 * Priority levels:
 * - Immediate: Red (red-500)
 * - Urgent: Orange (orange-500)
 * - Past Due: Pink (pink-500)
 * - Normal: Gray (gray-300)
 * - Low: Gray (gray-300)
 */
export const PriorityPill: React.FC<PriorityPillProps> = ({
  priority,
  size = 'sm',
  className = '',
}) => {
  const variant = PRIORITY_VARIANTS[priority.toLowerCase()] || 'gray';

  return (
    <Pill variant={variant} size={size} className={className}>
      {priority}
    </Pill>
  );
};
